import React, { createContext, useContext, useState, useEffect } from 'react';
import { getJSON, setJSON } from '../lib/storage';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Defensive read: corrupted localStorage cart no longer crashes the app
  // — we silently fall back to an empty cart and clear the bad entry.
  const [cart, setCart] = useState(() => {
    const saved = getJSON('cart', []);
    return Array.isArray(saved) ? saved : [];
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Sprint F+G — server-resolved per-line prices. Refetched whenever cart
  // contents change. Indexed by product_id (variant_id can be added later).
  const [resolvedPrices, setResolvedPrices] = useState({});  // { [product_id]: { final, base, tier, breakdown } }

  useEffect(() => { setJSON('cart', cart); }, [cart]);

  // Re-resolve prices whenever the cart changes. Debounced so rapid
  // qty bumps don't trigger N requests.
  useEffect(() => {
    if (!cart.length) { setResolvedPrices({}); return; }
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        const r = await fetch('/api/pricing/resolve-cart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json',
                     ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({
            items: cart.map(it => ({ product_id: it.id, quantity: it.quantity })),
          }),
        });
        if (!r.ok) return;
        const j = await r.json();
        const map = {};
        for (const line of (j.lines || [])) map[line.product_id] = line;
        setResolvedPrices(map);
      } catch (_) { /* keep stale resolved or empty */ }
    }, 300);
    return () => clearTimeout(t);
  }, [JSON.stringify(cart.map(c => [c.id, c.quantity]))]);

  // Get the per-unit price to use for display + totals. Prefer resolver
  // result; fall back to whatever was stored on the cart item.
  const priceFor = (item) => {
    const resolved = resolvedPrices[item.id];
    return resolved?.final !== undefined ? resolved.final : item.price;
  };

  // Cart key combines product id + variant id so different conditions of the
  // same card don't collapse into one line.
  const keyOf = (item) => `${item.id}::${item.variant_id || ''}`;

  // Sprint D — pre-check the customer's lifetime purchase limit before
  // committing the add. Returns { ok, qty, error?, warning? }.
  const addToCart = async (product, quantity = 1) => {
    let allowedQty = quantity;
    let warning = null;
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      if (token) {
        const r = await fetch(`/api/purchase-limits/effective?product_id=${product.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const j = await r.json();
          if (j?.most_restrictive) {
            const existing = cart.find(it => keyOf(it) === keyOf(product))?.quantity || 0;
            const remaining = j.most_restrictive.remaining;
            if (existing + quantity > remaining) {
              const space = Math.max(0, remaining - existing);
              if (space === 0) {
                return { ok: false, qty: 0, error: `You've reached your lifetime limit (${j.most_restrictive.max}) on this ${j.most_restrictive.scope}.` };
              }
              allowedQty = space;
              warning = `Capped at ${space} — your lifetime limit is ${j.most_restrictive.max}.`;
            }
          }
        }
      }
    } catch (_) { /* fail-open — server still validates at order create */ }

    setCart(prevCart => {
      const incomingKey = keyOf(product);
      const existingItem = prevCart.find(item => keyOf(item) === incomingKey);
      if (existingItem) {
        return prevCart.map(item =>
          keyOf(item) === incomingKey
            ? { ...item, quantity: item.quantity + allowedQty }
            : item
        );
      }
      return [...prevCart, { ...product, quantity: allowedQty }];
    });
    setDrawerOpen(true);
    return { ok: true, qty: allowedQty, warning };
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(prevCart =>
        prevCart.map(item =>
          item.id === productId ? { ...item, quantity } : item
        )
      );
    }
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (priceFor(item) * item.quantity), 0);
  };

  const getCartItemsCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartItemsCount,
        priceFor,                  // Sprint F+G — per-unit resolved price
        resolvedPrices,            // line-by-line breakdown (tier, markup steps)
        drawerOpen,
        openDrawer: () => setDrawerOpen(true),
        closeDrawer: () => setDrawerOpen(false),
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
